/**
 * `/admin/audit` — the filterable log (SPEC §9.1, §6).
 *
 * Filters live in the URL for the same reason report parameters do: a filtered
 * view of the audit log is something people send each other.
 *
 * Parameter values are shown here and nowhere else. They are stored in an
 * access-controlled table rather than in the application log precisely so this
 * page can be the one place they are readable (§12.9).
 */

import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'

import { Badge, Button, Card, Grid, TextField } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { adminApi } from '../../lib/admin'
import type { EventType } from '../../lib/adminTypes'
import { formatDateTime, formatInteger } from '../../lib/format'

const PAGE_SIZE = 50
const EVENT_TYPES: readonly EventType[] = [
  'login',
  'report_view',
  'export',
  'admin_change',
  'utility_commit',
]

export function AdminAudit() {
  const [searchParams, setSearchParams] = useSearchParams()
  const report = searchParams.get('report') ?? ''
  const eventType = searchParams.get('event_type') ?? ''
  const from = searchParams.get('from') ?? ''
  const to = searchParams.get('to') ?? ''
  const offset = Number(searchParams.get('offset') ?? '0')

  function setParam(key: string, value: string): void {
    const next = new URLSearchParams(searchParams)
    if (value === '') next.delete(key)
    else next.set(key, value)
    // Any filter change returns to the first page; keeping the offset would
    // land on an empty page of a shorter result set.
    if (key !== 'offset') next.delete('offset')
    setSearchParams(next, { replace: true })
  }

  const page = useQuery({
    queryKey: ['admin', 'audit', report, eventType, from, to, offset],
    queryFn: () =>
      adminApi.audit({
        report: report || undefined,
        event_type: eventType === '' ? undefined : (eventType as EventType),
        from: from || undefined,
        to: to || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  })

  return (
    <Card title="Audit log" description="Newest first. Every report run and every admin change.">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-56">
          <TextField
            label="Report slug"
            value={report}
            onChange={(event) => {
              setParam('report', event.target.value)
            }}
          />
        </div>
        <label className="text-[length:var(--text-xs)] font-medium">
          <span className="block">Event type</span>
          <select
            value={eventType}
            onChange={(event) => {
              setParam('event_type', event.target.value)
            }}
            className="mt-1 rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface)] px-2 py-1.5 text-[length:var(--text-sm)]"
          >
            <option value="">All</option>
            {EVENT_TYPES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <div className="w-44">
          <TextField
            label="From"
            type="datetime-local"
            value={from}
            onChange={(event) => {
              setParam('from', event.target.value)
            }}
          />
        </div>
        <div className="w-44">
          <TextField
            label="To"
            type="datetime-local"
            value={to}
            onChange={(event) => {
              setParam('to', event.target.value)
            }}
          />
        </div>
      </div>

      {page.isPending ? (
        <Skeleton rows={6} />
      ) : page.error !== null ? (
        <ErrorState
          error={page.error}
          onRetry={() => {
            void page.refetch()
          }}
        />
      ) : (
        <>
          <Grid head={['When', 'User', 'Event', 'Report', 'Outcome', 'Rows', 'Duration', 'Detail']}>
            {page.data.events.map((event) => (
              <tr key={event.id} className="border-b border-[var(--color-rule)] last:border-0">
                <td className="numeric px-2 py-1.5 text-[length:var(--text-xs)] whitespace-nowrap">
                  {formatDateTime(event.created_at)}
                </td>
                <td className="numeric px-2 py-1.5 text-[length:var(--text-xs)]">
                  {event.user_upn ?? '—'}
                </td>
                <td className="px-2 py-1.5">
                  <Badge tone={event.event_type === 'admin_change' ? 'warning' : 'neutral'}>
                    {event.event_type}
                  </Badge>
                </td>
                <td className="numeric px-2 py-1.5 text-[length:var(--text-xs)]">
                  {event.report_slug ?? '—'}
                </td>
                <td className="px-2 py-1.5 text-[length:var(--text-xs)]">
                  {event.status === null ? (
                    '—'
                  ) : (
                    <Badge tone={event.status === 'ok' ? 'positive' : 'neutral'}>
                      {event.status}
                    </Badge>
                  )}
                </td>
                <td className="numeric px-2 py-1.5 text-right text-[length:var(--text-xs)]">
                  {event.row_count === null ? '—' : formatInteger(event.row_count)}
                </td>
                <td className="numeric px-2 py-1.5 text-right text-[length:var(--text-xs)]">
                  {event.duration_ms === null ? '—' : `${formatInteger(event.duration_ms)} ms`}
                  {event.cache_hit === true && ' (cached)'}
                </td>
                <td className="px-2 py-1.5">
                  {event.params === null ? (
                    '—'
                  ) : (
                    <details>
                      <summary className="cursor-pointer text-[length:var(--text-xs)]">
                        params
                      </summary>
                      <pre className="numeric mt-1 max-w-md overflow-x-auto text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                        {JSON.stringify(event.params, null, 2)}
                      </pre>
                    </details>
                  )}
                </td>
              </tr>
            ))}
          </Grid>

          <div className="mt-3 flex items-center gap-3 text-[length:var(--text-xs)]">
            <span className="numeric text-[var(--color-ink-muted)]">
              {page.data.total === 0
                ? 'No events'
                : `${formatInteger(offset + 1)}–${formatInteger(
                    Math.min(offset + page.data.events.length, page.data.total),
                  )} of ${formatInteger(page.data.total)}`}
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                disabled={offset === 0}
                onClick={() => {
                  setParam('offset', String(Math.max(0, offset - PAGE_SIZE)))
                }}
              >
                Previous
              </Button>
              <Button
                disabled={offset + PAGE_SIZE >= page.data.total}
                onClick={() => {
                  setParam('offset', String(offset + PAGE_SIZE))
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  )
}
